import { useCallback, useMemo } from "react";
import { startOfYesterday, isAfter, isValid } from "date-fns";

import { useAuthUserQuery } from "../../user/hooks/queries/useAuthUserQuery";
import { useNotificationsQuery } from "./queries/useNotificationsQuery";

const newNotificationsStartDate = startOfYesterday();

export const useNotifications = () => {
  const { data: userData } = useAuthUserQuery();
  const { data: notificationsData } = useNotificationsQuery();

  const isNewNotification = useCallback(
    notification => {
      const isAfterStartDate = isAfter(
        new Date(notification.read_at),
        newNotificationsStartDate
      );

      const isSameSession =
        notification.read_session === userData.session_token;

      return isAfterStartDate && isSameSession;
    },
    [userData.session_token]
  );

  const oldNotifications = useMemo(
    () =>
      notificationsData.filter(item => {
        if (
          item.read_at &&
          item.read_session &&
          isValid(new Date(item.read_at))
        ) {
          return !isNewNotification(item);
        }

        return true;
      }),
    [isNewNotification, notificationsData]
  );

  const newNotifications = useMemo(
    () =>
      notificationsData.filter(item => {
        if (
          item.read_at &&
          item.read_session &&
          isValid(new Date(item.read_at))
        ) {
          return isNewNotification(item);
        }

        return false;
      }),
    [isNewNotification, notificationsData]
  );

  const unreadNotifications = useMemo(
    () => notificationsData.filter(item => !item.read_at),
    [notificationsData]
  );

  const unvisitedNotifications = useMemo(
    () => notificationsData.filter(item => !item.visited),
    [notificationsData]
  );

  return {
    oldNotifications,
    newNotifications,
    unreadNotifications,
    unvisitedNotifications
  };
};
